import type { CSSProperties } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  CalendarEvent,
  CalendarIntegration,
  NoticeRoomCardLayer,
} from '@cornerstone/shared';
import { ROOM_CARD_THEME_TOKENS } from '@cornerstone/shared';

const DESIGN_WIDTH = 1920;
const DESIGN_HEIGHT = 1080;
const MAX_CARDS = 5;
const SEOUL_OFFSET_MS = 9 * 60 * 60 * 1000;

export type RoomCardProps = {
  layer: NoticeRoomCardLayer;
  room: CalendarIntegration | undefined;
  events: CalendarEvent[];
  nowMs: number;
  canvasWidth?: number;
  canvasHeight?: number;
};

export function RoomCard({
  layer,
  room,
  events,
  nowMs,
  canvasWidth = DESIGN_WIDTH,
  canvasHeight = DESIGN_HEIGHT,
}: RoomCardProps): JSX.Element {
  const tokens = ROOM_CARD_THEME_TOKENS[layer.theme];
  const picked = useMemo(() => pickRoomCardEvents(events, nowMs), [events, nowMs]);
  const title = resolveRoomCardTitle(layer, room);

  const style: CSSProperties = {
    position: 'relative',
    width: canvasWidth,
    height: canvasHeight,
    overflow: 'hidden',
    background: tokens.bg,
    color: tokens.cardText,
    fontFamily: 'PretendardVariable, Inter Tight, system-ui, sans-serif',
  };

  const scaleX = canvasWidth / DESIGN_WIDTH;
  const scaleY = canvasHeight / DESIGN_HEIGHT;

  if (layer.theme === 'hanoi') {
    return (
      <HanoiRoomCard
        layer={layer}
        title={title}
        events={picked}
        nowMs={nowMs}
        tokens={tokens}
        canvasWidth={canvasWidth}
        canvasHeight={canvasHeight}
      />
    );
  }

  return (
    <div style={style}>
      <div
        style={{
          position: 'absolute',
          left: 80 * scaleX,
          top: 56 * scaleY,
          width: 980 * scaleX,
          height: 96 * scaleY,
          display: 'flex',
          alignItems: 'center',
          color: tokens.headerText,
          fontWeight: 700,
          fontSize: 56 * scaleY,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        {title}
      </div>
      <div
        style={{
          position: 'absolute',
          right: 80 * scaleX,
          top: 184 * scaleY,
          width: 720 * scaleX,
          height: 600 * scaleY,
          border: `2px dashed ${tokens.headerText}`,
          borderRadius: 40 * scaleY,
          opacity: 0.28,
        }}
      />
      {layer.showLogo ? (
        <div
          style={{
            position: 'absolute',
            right: 80 * scaleX,
            bottom: 76 * scaleY,
            width: 720 * scaleX,
            height: 184 * scaleY,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: tokens.headerText,
            fontWeight: 700,
            fontSize: 52 * scaleY,
            letterSpacing: 4 * scaleX,
            opacity: 0.72,
          }}
        >
          BRICK TODAY
        </div>
      ) : null}
      {picked.length === 0 ? (
        <EmptyState scaleX={scaleX} scaleY={scaleY} color={tokens.cardText} />
      ) : (
        picked.map((event, idx) => (
          <EventCard
            key={event.id}
            event={event}
            idx={idx}
            nowMs={nowMs}
            scaleX={scaleX}
            scaleY={scaleY}
            tokens={tokens}
          />
        ))
      )}
    </div>
  );
}

function HanoiRoomCard({
  layer,
  title,
  events,
  nowMs,
  tokens,
  canvasWidth,
  canvasHeight,
}: {
  layer: NoticeRoomCardLayer;
  title: string;
  events: CalendarEvent[];
  nowMs: number;
  tokens: (typeof ROOM_CARD_THEME_TOKENS)[keyof typeof ROOM_CARD_THEME_TOKENS];
  canvasWidth: number;
  canvasHeight: number;
}): JSX.Element {
  const scaleX = canvasWidth / DESIGN_WIDTH;
  const scaleY = canvasHeight / DESIGN_HEIGHT;
  const grid = `${48 * scaleX}px ${48 * scaleY}px`;

  return (
    <div
      style={{
        position: 'relative',
        width: canvasWidth,
        height: canvasHeight,
        overflow: 'hidden',
        backgroundColor: tokens.bg,
        backgroundImage: `linear-gradient(${tokens.timeBlock} 1px, transparent 1px), linear-gradient(90deg, ${tokens.timeBlock} 1px, transparent 1px)`,
        backgroundSize: grid,
        fontFamily: 'PretendardVariable, SUIT, sans-serif',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: 92 * scaleX,
          top: 63 * scaleY,
          width: 467 * scaleX,
          height: 97 * scaleY,
          color: tokens.headerText,
          fontSize: 81 * scaleY,
          lineHeight: `${97 * scaleY}px`,
          fontWeight: 600,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        {title}
      </div>
      <HanoiStickers scaleX={scaleX} scaleY={scaleY} />
      {layer.showLogo ? <HanoiLogo scaleX={scaleX} scaleY={scaleY} /> : null}
      {events.length === 0 ? (
        <HanoiEmptyState scaleX={scaleX} scaleY={scaleY} tokens={tokens} />
      ) : (
        events.map((event, idx) => (
          <HanoiEventCard
            key={event.id}
            event={event}
            idx={idx}
            nowMs={nowMs}
            scaleX={scaleX}
            scaleY={scaleY}
            tokens={tokens}
          />
        ))
      )}
    </div>
  );
}

function HanoiEventCard({
  event,
  idx,
  nowMs,
  scaleX,
  scaleY,
  tokens,
}: {
  event: CalendarEvent;
  idx: number;
  nowMs: number;
  scaleX: number;
  scaleY: number;
  tokens: (typeof ROOM_CARD_THEME_TOKENS)[keyof typeof ROOM_CARD_THEME_TOKENS];
}): JSX.Element {
  const top = (259 + idx * (133 + 20)) * scaleY;
  const ongoing = event.startAt <= nowMs && event.endAt > nowMs;

  return (
    <div
      style={{
        position: 'absolute',
        left: 64 * scaleX,
        top,
        width: 842 * scaleX,
        height: 133 * scaleY,
        display: 'flex',
        overflow: 'hidden',
        background: tokens.cardBg,
        border: `${ongoing ? 4 : 2}px solid #1350A3`,
        borderRadius: 15 * scaleY,
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          width: 240 * scaleX,
          height: '100%',
          background: tokens.timeBlock,
          color: tokens.timeBlockText,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 33 * scaleY,
          fontWeight: 700,
          fontFamily: 'SUIT, PretendardVariable, sans-serif',
          whiteSpace: 'nowrap',
        }}
      >
        {formatAmpm(event.startAt)} - {formatAmpm(event.endAt)}
      </div>
      <div
        style={{
          flex: 1,
          padding: `${24 * scaleY}px ${34 * scaleX}px`,
          color: tokens.cardText,
          fontSize: 32 * scaleY,
          lineHeight: `${40 * scaleY}px`,
          fontWeight: 700,
          fontFamily: 'SUIT, PretendardVariable, sans-serif',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {event.summary || '(제목 없음)'}
      </div>
    </div>
  );
}

function HanoiEmptyState({
  scaleX,
  scaleY,
  tokens,
}: {
  scaleX: number;
  scaleY: number;
  tokens: (typeof ROOM_CARD_THEME_TOKENS)[keyof typeof ROOM_CARD_THEME_TOKENS];
}): JSX.Element {
  return (
    <div
      style={{
        position: 'absolute',
        left: 64 * scaleX,
        top: 259 * scaleY,
        width: 842 * scaleX,
        height: 133 * scaleY,
        border: `${2 * scaleY}px solid #1350A3`,
        borderRadius: 15 * scaleY,
        background: tokens.cardBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: tokens.cardText,
        fontSize: 32 * scaleY,
        fontWeight: 700,
      }}
    >
      등록된 일정이 없습니다
    </div>
  );
}

function HanoiLogo({ scaleX, scaleY }: { scaleX: number; scaleY: number }): JSX.Element {
  const layers = [
    { x: 1106, y: 380, color: '#E19FFB' },
    { x: 1112, y: 374, color: '#E19FFB' },
    { x: 1118, y: 368, color: '#E19FFB' },
    { x: 1124, y: 362, color: '#E19FFB' },
    { x: 1130, y: 356, color: '#FAF2DD' },
  ];
  return (
    <>
      <div
        style={{
          position: 'absolute',
          left: 1040 * scaleX,
          top: 250 * scaleY,
          width: 770 * scaleX,
          height: 560 * scaleY,
          background: '#FCF1DB',
          clipPath: 'polygon(50% 0%, 58% 28%, 85% 12%, 70% 38%, 100% 45%, 70% 55%, 88% 82%, 60% 68%, 50% 100%, 40% 68%, 12% 82%, 30% 55%, 0% 45%, 30% 38%, 15% 12%, 42% 28%)',
        }}
      />
      {layers.map((layer, idx) => (
        <div
          key={idx}
          style={{
            position: 'absolute',
            left: layer.x * scaleX,
            top: layer.y * scaleY,
            color: layer.color,
            WebkitTextStroke: `${3 * scaleY}px #422D32`,
            fontSize: 137 * scaleY,
            lineHeight: `${145 * scaleY}px`,
            fontWeight: 900,
            letterSpacing: -5 * scaleX,
            whiteSpace: 'pre-line',
          }}
        >
          {'Brick\ntoday'}
        </div>
      ))}
    </>
  );
}

function HanoiStickers({ scaleX, scaleY }: { scaleX: number; scaleY: number }): JSX.Element {
  return (
    <>
      <div style={{ position: 'absolute', left: 1165 * scaleX, top: 270 * scaleY, width: 80 * scaleX, height: 72 * scaleY, background: '#6BB6D4', border: `${4 * scaleY}px solid #1350A3`, borderRadius: '58% 42% 52% 48%', transform: 'rotate(-12deg)' }} />
      <div style={{ position: 'absolute', left: 1065 * scaleX, top: 520 * scaleY, width: 180 * scaleX, height: 34 * scaleY, background: '#6BB6D4', border: `${4 * scaleY}px solid #1350A3`, borderRadius: 14 * scaleY, transform: 'rotate(-22deg)' }} />
      <div style={{ position: 'absolute', left: 1120 * scaleX, top: 800 * scaleY, width: 92 * scaleX, height: 120 * scaleY, background: '#FAC5CE', border: `${5 * scaleY}px solid #422D32`, clipPath: 'polygon(50% 0%, 100% 82%, 0% 82%)' }} />
      <div style={{ position: 'absolute', left: 1700 * scaleX, top: 560 * scaleY, width: 136 * scaleX, height: 122 * scaleY, background: '#FE6F38', border: `${5 * scaleY}px solid #422D32`, clipPath: 'polygon(50% 0%, 100% 100%, 0% 100%)' }} />
      <div style={{ position: 'absolute', left: 1660 * scaleX, top: 720 * scaleY, width: 120 * scaleX, height: 96 * scaleY, background: '#FAF2DD', border: `${5 * scaleY}px solid #1350A3`, clipPath: 'polygon(0% 30%, 80% 0%, 80% 100%, 0% 70%)' }} />
    </>
  );
}

function EventCard({
  event,
  idx,
  nowMs,
  scaleX,
  scaleY,
  tokens,
}: {
  event: CalendarEvent;
  idx: number;
  nowMs: number;
  scaleX: number;
  scaleY: number;
  tokens: (typeof ROOM_CARD_THEME_TOKENS)[keyof typeof ROOM_CARD_THEME_TOKENS];
}): JSX.Element {
  const top = (184 + idx * (148 + 20)) * scaleY;
  const ongoing = event.startAt <= nowMs && event.endAt > nowMs;

  return (
    <div
      style={{
        position: 'absolute',
        left: 80 * scaleX,
        top,
        width: 980 * scaleX,
        height: 148 * scaleY,
        borderRadius: 24 * scaleY,
        overflow: 'hidden',
        background: tokens.cardBg,
        display: 'flex',
        boxShadow: ongoing ? `0 0 0 ${4 * scaleY}px ${tokens.timeBlock}` : undefined,
      }}
    >
      <div
        style={{
          width: 240 * scaleX,
          height: '100%',
          background: tokens.timeBlock,
          color: tokens.timeBlockText,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
        }}
      >
        <div style={{ fontSize: 36 * scaleY }}>{formatHm(event.startAt)}</div>
        <div style={{ fontSize: 22 * scaleY, fontWeight: 500 }}>
          ~{formatHm(event.endAt)}
        </div>
      </div>
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          padding: `0 ${24 * scaleX}px`,
          color: tokens.cardText,
          fontSize: 32 * scaleY,
          fontWeight: ongoing ? 700 : 500,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {event.summary || '(제목 없음)'}
      </div>
    </div>
  );
}

function EmptyState({
  scaleX,
  scaleY,
  color,
}: {
  scaleX: number;
  scaleY: number;
  color: string;
}): JSX.Element {
  return (
    <div
      style={{
        position: 'absolute',
        left: 80 * scaleX,
        top: 184 * scaleY,
        width: 980 * scaleX,
        height: 820 * scaleY,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color,
        opacity: 0.6,
        fontSize: 36 * scaleY,
        fontWeight: 500,
      }}
    >
      등록된 일정이 없습니다
    </div>
  );
}

export function pickRoomCardEvents(
  events: CalendarEvent[],
  nowMs: number,
): CalendarEvent[] {
  const { startMs, endMs } = seoulDayBoundsMs(nowMs);
  const today = events
    .filter((e) => e.status !== 'cancelled')
    .filter((e) => e.startAt < endMs && e.endAt > startMs)
    .filter((e) => e.endAt > nowMs)
    .sort((a, b) => a.startAt - b.startAt);

  const ongoingIdx = today.findIndex(
    (e) => e.startAt <= nowMs && e.endAt > nowMs,
  );
  if (ongoingIdx === -1) return today.slice(0, MAX_CARDS);
  const ongoing = today[ongoingIdx]!;
  const rest = today.filter((_, i) => i !== ongoingIdx);
  return [ongoing, ...rest].slice(0, MAX_CARDS);
}

export function resolveRoomCardTitle(
  layer: NoticeRoomCardLayer,
  room: CalendarIntegration | undefined,
): string {
  const explicit = layer.title?.trim();
  if (explicit) return explicit;
  const label = room?.label.trim();
  if (label) return `${cleanLabel(label)} Today`;
  return 'Meeting Room Today';
}

export type RoomCardScaledBoxProps = RoomCardProps & {
  className?: string;
  style?: CSSProperties;
};

export function RoomCardScaledBox({
  className,
  style,
  ...props
}: RoomCardScaledBoxProps): JSX.Element {
  const ref = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const intrinsicW = props.canvasWidth ?? DESIGN_WIDTH;
  const intrinsicH = props.canvasHeight ?? DESIGN_HEIGHT;

  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const rect = entry.contentRect;
      setSize({ w: rect.width, h: rect.height });
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  const scale = Math.min(size.w / intrinsicW, size.h / intrinsicH) || 1;

  return (
    <div
      ref={ref}
      className={className}
      style={{ position: 'relative', overflow: 'hidden', ...style }}
    >
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          width: intrinsicW,
          height: intrinsicH,
          transform: `translate(-50%, -50%) scale(${scale})`,
          transformOrigin: 'center center',
        }}
      >
        <RoomCard {...props} />
      </div>
    </div>
  );
}

function seoulDayBoundsMs(nowMs: number): { startMs: number; endMs: number } {
  const seoulNow = nowMs + SEOUL_OFFSET_MS;
  const dayStartSeoul = Math.floor(seoulNow / 86_400_000) * 86_400_000;
  return {
    startMs: dayStartSeoul - SEOUL_OFFSET_MS,
    endMs: dayStartSeoul - SEOUL_OFFSET_MS + 86_400_000,
  };
}

function formatHm(ms: number): string {
  const seoul = new Date(ms + SEOUL_OFFSET_MS);
  const h = String(seoul.getUTCHours()).padStart(2, '0');
  const m = String(seoul.getUTCMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

function formatAmpm(ms: number): string {
  const seoul = new Date(ms + SEOUL_OFFSET_MS);
  const hour = seoul.getUTCHours();
  const suffix = hour < 12 ? 'AM' : 'PM';
  const h = hour % 12 || 12;
  return `${h}${suffix}`;
}

function cleanLabel(label: string): string {
  return label.replace(/\s*\(\d+\)\s*$/, '');
}
